import {
  Component,
  EventEmitter,
  OnInit,
  Input,
  Output,
  OnDestroy,
} from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { RegexPatterns } from "./../../../shared/regex/regex-patterns";
import { UserService } from "./../../../shared/services/user.service";
import { PractitionerService } from "./../../shared/services/practitioner.service";
import { NotificationsService } from "./../../../shared/services/notifications.service";
import { ViewStateService } from "./../../../shared/services/view-state.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-import-course",
  templateUrl: "./import-course.component.html",
})
export class ImportCourseComponent implements OnInit, OnDestroy {
  importCoursesForm: FormGroup;
  formLevelErrors: String[] = [];
  personId: string;
  private destroy$ = new Subject<void>();

  @Output() onCancelForm: EventEmitter<boolean> = new EventEmitter();
  @Output() onImportCourse: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private viewState: ViewStateService,
    private practitioner: PractitionerService,
    private userService: UserService,
    private notifications: NotificationsService
  ) {}

  ngOnInit() {
    this.buildForm();
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.personId = this.userService.CurrentUser.personId;
    this.formValues = updatedFormValues;
    this.validateForm();
    this.importCourses();
  }

  onCancel(event: Event) {
    event.preventDefault();

    this.onCancelForm.emit();
  }

  private buildForm(): void {
    this.importCoursesForm = this.formBuilder.group({
      aarcNumber: [
        this.formValues.aarcNumber,
        [
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
    });
  }

  private validateForm() {
    this.formLevelErrors = [];

    for (const control in this.importCoursesForm.controls) {
      this.importCoursesForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.importCoursesForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }

    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private mapServerErrorsToForm(errors) {
    for (let error of errors) {
      this.formErrors[error.fieldName] = "";
      const control = this.importCoursesForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) this.formLevelErrors.push(error.message);
    }
  }

  private importCourses() {
    if (this.importCoursesForm.valid) {
      this.practitioner
        .importCourse(this.personId, this.importCoursesForm.value.aarcNumber)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.onImportCourse.emit();
            this.notifications.success("Success", "The course was imported.");
            this.viewState.stopLoading();
          },
          (errors) => {
            this.handleServerErrorResponse(errors);
            this.viewState.stopLoading();
          }
        );
    }
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500)
      this.notifications.error(
        "Unexpected Error",
        "An unexpected error occurred. Please try again or contact NBRC for assistance."
      );
    else {
      this.mapServerErrorsToForm(response.error);
      this.notifications.error(
        "Import Failed",
        "No courses found to import. Contact AARC for assistance."
      );
    }
    this.viewState.stopLoading();
  }

  private formValues = {
    aarcNumber: "",
  };

  formErrors = {
    aarcNumber: "",
  };

  validationMessages = {
    aarcNumber: {
      required: "AARC Number is required.",
      maxlength: "AARC must be less than 16 digits.",
      pattern: "AARC must be numbers only.",
    },
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
